import { useEffect } from "react";
import { cartState, mobileRegistryModalState, scheduleState, toastState, appointmentsState } from "atoms";
import { useRecoilState } from "recoil";
import { Modal } from "react-bootstrap";
import { getSchedulePage } from "services/get-getSchedulePage";
import { postSchedulePage } from "services/post-postSchedulePage";
import { useWrapRequest, formatPhoneNumber, removeParenthesisAndDashes } from "utils";
import { useNavigate } from "react-router-dom";
import { getAppointments } from "services/get-getAppointments";
import ScheduleRadioButtons from "components/Schedule/ScheduleRadioButtons";
import CartInfo from "components/Customize/CartInfo";
import ServiceAndInstallationButtonContainer from "components/Schedule/ServiceAndInstallationButtonContainer";
import { OPTION_PRO_INSTALL } from "utils/const";
import { ScheduleModel } from "atoms/models";
import "components/Schedule/schedule.scss";

export interface AppointmentsModel {
  schedules: Schedules;
}

interface Schedules {
  securitySchedule: SecuritySchedule;
  nonSecuritySchedule: NonSecuritySchedule;
}

interface NonSecuritySchedule {
  availableAppointments: AvailableAppointment[];
}

interface AvailableAppointment {
  appointment: Appointment2;
}

interface Appointment2 {
  workDate: string;
  workTime: WorkTime[];
}

interface WorkTime {
  appointment: Appointment;
}

interface Appointment {
  time: string;
  friendlyTime: string;
}

interface SecuritySchedule {
  availableAppointments: SecuritySchedule[];
}

const Schedule = () => {
  const [cart, setCart] = useRecoilState(cartState);
  const [toast, setToast] = useRecoilState(toastState);
  const [appointments, setAppointments] = useRecoilState(appointmentsState);
  const [showMobileRegistryModal, setShowMobileRegistryModal] = useRecoilState(mobileRegistryModalState);
  const [schedule, setSchedule] = useRecoilState(scheduleState);
  const wrapRequest = useWrapRequest();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSchedulePage = () => {
      wrapRequest(async () => {
        const getSchedulePageResponse = await getSchedulePage();
        const links = getSchedulePageResponse["_links"];
        const windowLocation = "ec:windowLocation";

        if (getSchedulePageResponse._embedded) {
          const appointments = await getAppointments();
          const cart = getSchedulePageResponse._embedded["ec:cart"];

          setCart(cart);
          setAppointments(appointments);
          if (cart?.schedule?.availableInstallTypes.includes(OPTION_PRO_INSTALL) && cart?.schedule?.availableInstallTypes?.length === 1) {
            setSchedule((prevSchedule: ScheduleModel) => ({
              ...prevSchedule,
              optionSelected: cart.schedule.availableInstallTypes[0],
              billCycle: cart.billCycle,
            }));
          }
          setSchedule((prevSchedule: ScheduleModel) => ({ ...prevSchedule, installationTextNumber: cart.phoneNumber, billCycle: cart?.billCycle }));
        } else {
          navigate(links[windowLocation].href);
        }
      });
    };
    fetchSchedulePage();

    const setInstallationTextPhoneNumber = () => {
      if (cart?.phoneNumber) {
        setSchedule((prevSchedule: ScheduleModel) => ({ ...prevSchedule, installationTextNumber: cart.phoneNumber }));
      }
    };
    setInstallationTextPhoneNumber();
    // eslint-disable-next-line
  }, [cart.phoneNumber, setSchedule, navigate, setCart]);

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  const securityAppointmentWasNotSelected =
    schedule.optionSelected === "PRO_INSTALL" &&
    appointments?.schedules?.securitySchedule?.availableAppointments.length > 0 &&
    (!schedule.securitySchedule.workDate || !schedule.securitySchedule.workTime);

  const appointmentWasNotSelected =
    schedule.optionSelected === "PRO_INSTALL" &&
    appointments?.schedules?.nonSecuritySchedule?.availableAppointments.length > 0 &&
    (!schedule.nonSecuritySchedule.workDate || !schedule.nonSecuritySchedule.workTime);

  const handleSubmit = () => {
    if (securityAppointmentWasNotSelected) {
      setToast({
        ...toast,
        show: true,
        variant: "danger",
        header: "Error",
        message:
          "Please choose a date and time for your installation appointment(s) before continuing. The installation date for your SECURITY product must be later than your NON SECURITY product.",
      });
    } else if (appointmentWasNotSelected) {
      setToast({
        ...toast,
        show: true,
        variant: "danger",
        header: "Error",
        message: "Please choose a date and time for your installation appointment(s) before continuing. Made a selection but did not select date",
      });
    } else {
      wrapRequest(async () => {
        const postSchedulePageResponse = await postSchedulePage(schedule);
        const links = postSchedulePageResponse["_links"];
        const windowLocation = "ec:windowLocation";

        if (links && links[windowLocation] && links[windowLocation].href) {
          navigate(links[windowLocation].href);
        } else {
          setToast({
            ...toast,
            show: true,
            variant: "danger",
            header: "Error",
            message: `${postSchedulePageResponse.message}`,
          });
        }
      });
    }
  };

  return (
    <div className="schedule-container">
      <div className="mt-5 me-3">
        <h2 className="service-installation-header mt-4">Service &amp; Installation</h2>
        <hr />
        {/* If OPTION_PRO_INSTALL (Professional Install) is the only one available */}
        {cart?.schedule?.availableInstallTypes.includes(OPTION_PRO_INSTALL) && cart?.schedule?.availableInstallTypes?.length === 1 ? (
          <ServiceAndInstallationButtonContainer appointments={appointments} />
        ) : (
          <ScheduleRadioButtons cart={cart} appointments={appointments} />
        )}
        {/* If security schedules are available show this message */}
        {appointments?.schedules?.securitySchedule?.availableAppointments.length > 0 ? (
          <div className="mt-5">* Appointment is subject to change based on number of devices to be installed.</div>
        ) : null}
        {schedule.nonSecuritySchedule.workTime || schedule.securitySchedule.workTime ? (
          <>
            <hr className="mt-5 mb-5" />
            <div className="text-reminder">
              <div className="row">
                <div className="col-md-6 col-sm-12">
                  <p>To Receive a text reminder the day before your installation, please enter a mobile number.</p>
                </div>
                <div className="col-md-6 col-sm-12">
                  <div className="form-floating">
                    <input
                      type="text"
                      className={
                        schedule.installationTextNumber.length > 0 && schedule.installationTextNumber.length < 10
                          ? "form-control is-invalid"
                          : "form-control is-valid"
                      }
                      placeholder="Phoen Number To Text"
                      value={formatPhoneNumber(schedule.installationTextNumber)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setSchedule({ ...schedule, installationTextNumber: removeParenthesisAndDashes(e.target.value) })
                      }
                      inputMode="numeric"
                    />
                    <label htmlFor="floatingPassword">Phone Number</label>
                    <div id="validationServer03Feedback" className="invalid-feedback">
                      Invalid Phone Number. Please enter a valid 10 digit number.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : null}
        <hr />
        <div className="btn-continue-container">
          <button
            type="submit"
            className="btn btn-default btn-continue mb-5"
            onClick={() => handleSubmit()}
            disabled={schedule.installationTextNumber.length > 0 && schedule.installationTextNumber.length < 10}
          >
            Continue
          </button>
        </div>
        <Modal show={showMobileRegistryModal} onHide={() => setShowMobileRegistryModal(false)}>
          <Modal.Header closeButton></Modal.Header>
          <Modal.Body>{Object.keys(cart).length > 1 ? <CartInfo cart={cart} onContinue={handleSubmit} /> : null}</Modal.Body>
        </Modal>
      </div>
      <div className="mt-5 schedule-register-container">
        {Object.keys(cart).length > 1 ? (
          <CartInfo
            cart={cart}
            onContinue={handleSubmit}
            disableContinueButton={schedule.installationTextNumber.length > 0 && schedule.installationTextNumber.length < 10}
          />
        ) : null}
      </div>
    </div>
  );
};

export default Schedule;
